// We should consider moving to https://primevue.org/dynamicdialog/ once everything is in Vue.
// Currently we need to bridge between legacy app code and Vue app with a Pinia store.
import { merge } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import type { DialogPassThroughOptions } from 'primevue/dialog'
import { markRaw, ref } from 'vue'
import type { Component, HTMLAttributes, Ref } from 'vue'

import type { DialogContentSize } from '@/components/ui/dialog/dialog.variants'
import type { ComponentAttrs } from 'vue-component-type-helpers'

type DialogPosition =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'

/**
 * Selects the dialog renderer used by `GlobalDialog`. `'reka'` (the default)
 * renders the Reka-UI primitive set under `src/components/ui/dialog/`.
 * `'primevue'` is the legacy PrimeVue `Dialog` escape hatch, kept only until
 * the branch is deleted in the Phase 6 cleanup (FE-578).
 */
type DialogRenderer = 'primevue' | 'reka'

interface CustomDialogComponentProps {
  maximizable?: boolean
  maximized?: boolean
  onClose?: () => void
  closable?: boolean
  modal?: boolean
  position?: DialogPosition
  pt?: DialogPassThroughOptions
  closeOnEscape?: boolean
  dismissableMask?: boolean
  /**
   * When `false`, the Reka dialog does not dismiss when focus leaves its
   * content. Set on container dialogs (e.g. Settings) that host nested dialogs,
   * where a nested dialog closing can move focus onto an ordinary app element
   * — a programmatic shift that must not be read as a dismiss. Escape and
   * outside-pointer dismissal are unaffected. Defaults to `true`.
   */
  dismissOnFocusOutside?: boolean
  unstyled?: boolean
  headless?: boolean
  renderer?: DialogRenderer
  size?: DialogContentSize
  /**
   * Class applied to the Reka-UI `DialogContent` element. Ignored on the
   * PrimeVue path — use `pt` for that renderer.
   */
  contentClass?: HTMLAttributes['class']
  /**
   * Class applied to the Reka-UI `DialogOverlay` element. Ignored on the
   * PrimeVue path — use `pt.mask` for that renderer.
   */
  overlayClass?: HTMLAttributes['class']
  /**
   * Class applied to the Reka-UI `DialogHeader` element on the non-headless
   * path. Ignored on the PrimeVue path — use `pt.header` for that renderer.
   */
  headerClass?: HTMLAttributes['class']
  /**
   * Class applied to the wrapper around the content component on the Reka-UI
   * non-headless path. Ignored on the PrimeVue path — use `pt.content` for
   * that renderer.
   */
  bodyClass?: HTMLAttributes['class']
  /**
   * Class applied to the Reka-UI `DialogFooter` element on the non-headless
   * path. Ignored on the PrimeVue path — use `pt.footer` for that renderer.
   */
  footerClass?: HTMLAttributes['class']
}

export type DialogComponentProps = Record<string, unknown> &
  CustomDialogComponentProps

export interface DialogInstance {
  key: string
  visible: boolean
  title?: string
  headerComponent?: Component
  headerProps?: Record<string, unknown>
  component: Component
  contentProps: Record<string, unknown>
  footerComponent?: Component
  footerProps?: Record<string, unknown>
  dialogComponentProps: DialogComponentProps
  priority: number
}

export interface ShowDialogOptions<
  H extends Component = Component,
  B extends Component = Component,
  F extends Component = Component
> {
  key?: string
  title?: string
  headerComponent?: H
  footerComponent?: F
  component: B
  props?: ComponentAttrs<B>
  headerProps?: ComponentAttrs<H>
  footerProps?: ComponentAttrs<F>
  dialogComponentProps?: DialogComponentProps
  /**
   * Optional priority for dialog stacking.
   * A dialog will never be shown above a dialog with a higher priority.
   * @default 1
   */
  priority?: number
}

interface UpdateDialogOptions {
  key: string
  contentProps?: Partial<DialogInstance['contentProps']>
  dialogComponentProps?: Partial<DialogComponentProps>
}

export const useDialogStore = defineStore('dialog', () => {
  const dialogStack: Ref<DialogInstance[]> = ref([])

  /**
   * The key of the currently active (top-most) dialog.
   * Only the active dialog can be closed with the ESC key.
   */
  const activeKey = ref<string | null>(null)

  const genDialogKey = () => `dialog-${Math.random().toString(36).slice(2, 9)}`

  /**
   * Inserts a dialog into the stack at the correct position based on priority.
   * Higher priority dialogs are placed before lower priority ones.
   */
  function insertDialogByPriority(dialog: DialogInstance) {
    const insertIndex = dialogStack.value.findIndex(
      (d) => d.priority <= dialog.priority
    )
    dialogStack.value.splice(
      insertIndex === -1 ? dialogStack.value.length : insertIndex,
      0,
      dialog
    )
  }

  function riseDialog(options: { key: string }) {
    const dialogKey = options.key

    const index = dialogStack.value.findIndex((d) => d.key === dialogKey)
    if (index !== -1) {
      const [dialog] = dialogStack.value.splice(index, 1)
      insertDialogByPriority(dialog)
      activeKey.value = dialogKey
      updateCloseOnEscapeStates()
    }
  }

  function closeDialog(options?: { key: string }) {
    const targetDialog = options
      ? dialogStack.value.find((d) => d.key === options.key)
      : dialogStack.value.find((d) => d.key === activeKey.value)
    if (!targetDialog) return

    targetDialog.dialogComponentProps?.onClose?.()
    const index = dialogStack.value.findIndex((d) => d.key === targetDialog.key)
    if (index !== -1) dialogStack.value.splice(index, 1)

    activeKey.value =
      dialogStack.value.length > 0
        ? dialogStack.value[dialogStack.value.length - 1].key
        : null

    updateCloseOnEscapeStates()
  }

  function createDialog<
    H extends Component = Component,
    B extends Component = Component,
    F extends Component = Component
  >(options: ShowDialogOptions<H, B, F> & { key: string }) {
    if (dialogStack.value.length >= 10) {
      dialogStack.value.shift()
    }

    const dialog = {
      key: options.key,
      visible: true,
      title: options.title,
      headerComponent: options.headerComponent
        ? markRaw(options.headerComponent)
        : undefined,
      footerComponent: options.footerComponent
        ? markRaw(options.footerComponent)
        : undefined,
      component: markRaw(options.component),
      headerProps: { ...options.headerProps },
      contentProps: { ...options.props },
      footerProps: { ...options.footerProps },
      priority: options.priority ?? 1,
      dialogComponentProps: {
        maximizable: false,
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        renderer: 'reka' as DialogRenderer,
        ...options.dialogComponentProps,
        maximized: false,
        onMaximize: () => {
          dialog.dialogComponentProps.maximized = true
        },
        onUnmaximize: () => {
          dialog.dialogComponentProps.maximized = false
        },
        onAfterHide: () => {
          closeDialog(dialog)
        },
        pt: merge(options.dialogComponentProps?.pt || {}, {
          root: {
            onMousedown: () => {
              riseDialog(dialog)
            }
          }
        })
      }
    }

    insertDialogByPriority(dialog)
    activeKey.value = options.key
    updateCloseOnEscapeStates()

    return dialog
  }

  /**
   * Ensures only the top-most dialog in the stack can be closed with the Escape key.
   * This is necessary because PrimeVue Dialogs do not handle `closeOnEscape` prop
   * correctly when multiple dialogs are open.
   */
  function updateCloseOnEscapeStates() {
    const topDialog = dialogStack.value.find((d) => d.key === activeKey.value)
    const topClosable = topDialog?.dialogComponentProps.closable

    dialogStack.value.forEach((dialog) => {
      dialog.dialogComponentProps = {
        ...dialog.dialogComponentProps,
        closeOnEscape: dialog === topDialog && !!topClosable
      }
    })
  }

  function showDialog<
    H extends Component = Component,
    B extends Component = Component,
    F extends Component = Component
  >(options: ShowDialogOptions<H, B, F>) {
    const dialogKey = options.key || genDialogKey()

    let dialog = dialogStack.value.find((d) => d.key === dialogKey)

    if (dialog) {
      dialog.visible = true
      riseDialog(dialog)
    } else {
      dialog = createDialog({ ...options, key: dialogKey })
    }
    return dialog
  }

  /**
   * Shows a dialog from a third party extension.
   * Explicitly keys extension dialogs with `extension-` prefix,
   * to avoid conflicts & prevent use of internal dialogs (available via `dialogService`).
   */
  function showExtensionDialog(options: ShowDialogOptions & { key: string }) {
    const { key } = options
    if (!key) {
      console.error('Extension dialog key is required')
      return
    }

    const extKey = key.startsWith('extension-') ? key : `extension-${key}`

    const dialog = dialogStack.value.find((d) => d.key === extKey)
    if (!dialog) return createDialog({ ...options, key: extKey })

    dialog.visible = true
    riseDialog(dialog)
    return dialog
  }

  function isDialogOpen(key: string) {
    return dialogStack.value.some((d) => d.key === key)
  }

  function updateDialog(options: UpdateDialogOptions): boolean {
    const dialog = dialogStack.value.find((d) => d.key === options.key)
    if (!dialog) return false

    if (options.contentProps) {
      dialog.contentProps = {
        ...dialog.contentProps,
        ...options.contentProps
      }
    }

    if (options.dialogComponentProps) {
      dialog.dialogComponentProps = {
        ...dialog.dialogComponentProps,
        ...options.dialogComponentProps
      }
      updateCloseOnEscapeStates()
    }

    return true
  }

  return {
    dialogStack,
    riseDialog,
    showDialog,
    closeDialog,
    showExtensionDialog,
    isDialogOpen,
    updateDialog,
    activeKey
  }
})
